import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiHome,
  FiGrid,
  FiMessageSquare,
  FiInfo,
  FiArrowRight,
  FiCheckCircle,
  FiXCircle,
  FiMessageCircle,
  FiUser,
  FiHelpCircle,
  FiSettings,
  FiDollarSign,
  FiX,
  FiShoppingBag,
  FiClock,
  FiSearch,
  FiPackage,
  FiBox,
  FiTruck,
  FiCheck,
} from 'react-icons/fi';
import TutorialTour from '../components/TutorialTour';
import socket, { reconnectSocketWithAuth, joinClienteRooms } from 'shared/socket';
import { setupClientePush, clientePushNecesario } from 'shared/pushNotifications';
import axios from 'axios';
import ClienteInicio from './ClienteInicio';
import ClienteCatalogo, { cssAnimations } from './ClienteCatalogo';
import ClienteChat from './ClienteChat';
import ClientePedidoDetalle from './ClientePedidoDetalle';
import ClientePedidoCorporativo from './ClientePedidoCorporativo';
import RegistroCliente from './RegistroCliente';
import LoginCliente from './LoginCliente';
import RegistroClienteCompleto from './RegistroClienteCompleto';
import RecuperarCliente from './RecuperarCliente';
import PerfilCliente from './PerfilCliente';
import ClienteCreditos from './ClienteCreditos';
import InstalarApp from 'shared/components/InstalarApp';
import CampanaNotificaciones from 'shared/components/CampanaNotificaciones';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import { playNotificationSound } from 'shared/notificationSound';
import { getSchemaName, getImagenUrl } from 'shared/api';
import useWindowSize from 'shared/hooks/useWindowSize';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Mensaje por fase del pedido: guia al cliente sobre que accion sigue y desde
// donde la gestiona (el detalle del pedido, icono abajo a la derecha).
const FASE_INFO = {
  pendiente: { texto: 'Confirma tu pedido para enviarlo', Icon: FiShoppingBag, color: '#FF9500' },
  en_revision: { texto: 'Un asesor está revisando tu pedido', Icon: FiSearch, color: '#FF9500' },
  aprobado: { texto: 'Elige tu método de pago', Icon: FiDollarSign, color: 'var(--color-secondary)' },
  esperando_pago: { texto: 'Carga tu comprobante de pago', Icon: FiDollarSign, color: '#FF9500' },
  pago_en_verificacion: { texto: 'Estamos verificando tu pago', Icon: FiClock, color: '#FF9500' },
  pagado: { texto: 'Pago confirmado, pasamos a la cocina', Icon: FiCheckCircle, color: 'var(--color-success)' },
  armado_cocina: { texto: 'Tu pedido está en la cocina', Icon: FiPackage, color: '#ff6d00' },
  armado: { texto: 'Tu pedido está en la cocina', Icon: FiPackage, color: '#ff6d00' },
  listo: { texto: '¡Casi listo! Tu pedido está por salir', Icon: FiBox, color: 'var(--color-secondary)' },
  despachado: { texto: 'Tu pedido ya fue despachado', Icon: FiTruck, color: '#00bcd4' },
  en_camino: { texto: 'Tu pedido viene en camino', Icon: FiTruck, color: '#00bcd4' },
  llegada: { texto: 'El delivery está llegando a tu puerta', Icon: FiTruck, color: '#009688' },
  entregado: { texto: '¡Listo! Pedido entregado', Icon: FiCheckCircle, color: 'var(--color-success)' },
};

function faseInfo(estado) {
  return FASE_INFO[estado] || FASE_INFO.pendiente;
}

const FASES_STEPPER = [
  { label: 'Pedido', estados: ['pendiente'] },
  { label: 'Revisión', estados: ['en_revision'] },
  { label: 'Pago', estados: ['aprobado', 'esperando_pago', 'pago_en_verificacion', 'pagado'] },
  { label: 'Preparación', estados: ['armado_cocina', 'armado', 'listo'] },
  { label: 'Envío', estados: ['despachado', 'en_camino', 'llegada'] },
  { label: 'Entregado', estados: ['entregado'] },
];

function faseStepIndex(estado) {
  const idx = FASES_STEPPER.findIndex((s) => s.estados.includes(estado));
  return idx >= 0 ? idx : 0;
}

function ClienteHome({ config }) {
  const schemaName = (config?.slug || getSchemaName() || 'negocio').replace(/-/g, '_');
  const companySlug = config?.slug || schemaName;
  const brandName = config?.nombre || 'Agorax';
  const botName = config?.nombre_bot || 'Asistente Virtual';
  const modoRegistro = config?.modo_registro || 'simple';
  const [activeTab, setActiveTab] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('producto');
    return p && config?.catalogo_habilitado !== false ? 'catalogo' : 'inicio';
  });
  const [productoCompartidoCodigo, setProductoCompartidoCodigo] = useState(
    () => new URLSearchParams(window.location.search).get('producto') || null
  );
  const [clienteData, setClienteData] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [cargandoPedidos, setCargandoPedidos] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [pedidoCatalogo, setPedidoCatalogo] = useState(null);
  const [verNuevos, setVerNuevos] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  const [verificandoRegistro, setVerificandoRegistro] = useState(true);
  const [pedidoCancelado, setPedidoCancelado] = useState(null);
  const [pedidoEntregado, setPedidoEntregado] = useState(false);
  const [pilotoDismissed, setPilotoDismissed] = useState(false);
  const [pendientesChat, setPendientesChat] = useState(0);
  const [toast, setToast] = useState(null);
  const [abrirConfirmacion, setAbrirConfirmacion] = useState(false);
  const [pedidoConfirmandoId, setPedidoConfirmandoId] = useState(null);
  const [tutorialActivo, setTutorialActivo] = useState(false);
  const [authScreen, setAuthScreen] = useState('login');
  const [recoveryTokenUrl, setRecoveryTokenUrl] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [pedidoSeleccionadoId, setPedidoSeleccionadoId] = useState(() => {
    const saved = localStorage.getItem(`agorax_pedido_seleccionado_${companySlug}`);
    return saved ? parseInt(saved, 10) : null;
  });
  const [modoNuevoPedido, setModoNuevoPedido] = useState(false);
  const [pushActivo, setPushActivo] = useState(() => !clientePushNecesario());
  const [pushSolicitando, setPushSolicitando] = useState(false);
  const toastTimerRef = useRef(null);

  const activarPush = async () => {
    setPushSolicitando(true);
    const ok = await setupClientePush(schemaName, sessionId);
    if (ok) setPushActivo(true);
    setPushSolicitando(false);
  };

  useEffect(() => {
    const favicon = document.getElementById('dynamic-favicon');
    if (!favicon) return;
    if (config?.logo_url) {
      const logoFull = getImagenUrl(config.logo_url);
      favicon.href = logoFull;
    }
  }, [config?.logo_url]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('recuperar_token');
    if (rt) {
      setRecoveryTokenUrl(rt);
      setAuthScreen('recover');
    }
  }, []);
  const { agregarNotificacion } = useNotificaciones();
  const { width: windowWidth } = useWindowSize();
  const isDesktop = windowWidth >= 768;
  const socketRef = useRef(null);
  const activeTabRef = useRef('inicio');
  const mainRef = useRef(null);
  const pullStartY = useRef(0);
  const clienteIdRef = useRef(null);
  const empresaIdRef = useRef(null);
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;
  const companyRef = useRef(companySlug);
  companyRef.current = companySlug;

  useEffect(() => {
    let sid = localStorage.getItem(`agorax_session_${companySlug}`);
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem(`agorax_session_${companySlug}`, sid);
    }
    setSessionId(sid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClienteInfo = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API_URL}/chat/cliente-info/${schemaName}/${sessionId}`);
      if (res.data.status === 'success' && res.data.data) {
        setClienteData(res.data.data);
        const cid = res.data.data.id;
        const eid = res.data.data.empresa_id;
        clienteIdRef.current = cid;
        empresaIdRef.current = eid;
        if (socketRef.current && socketRef.current.connected && cid) {
          joinClienteRooms(socketRef.current, { idcliente: cid, empresaId: eid, sessionId });
        }
      }
    } catch (e) {
      console.error(
        'Error fetchClienteInfo:',
        e.response?.status,
        e.response?.data?.detail || e.message
      );
    } finally {
      setVerificandoRegistro(false);
    }
  }, [sessionId, schemaName]);

  const fetchPedidos = useCallback(async () => {
    const sid = localStorage.getItem(`agorax_session_${companySlug}`);
    if (!sid) return;
    try {
      setCargandoPedidos(true);
      const res = await axios.get(`${API_URL}/pedidos/mis-pedidos/${schemaName}/${sid}`);
      if (res.data.status === 'success') setPedidos(res.data.data || []);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setCargandoPedidos(false);
    }
  }, [companySlug, schemaName]);

  useEffect(() => {
    if (sessionId) {
      fetchClienteInfo();
      fetchPedidos();
    }
  }, [sessionId, fetchClienteInfo, fetchPedidos, schemaName]);

  useEffect(() => {
    if (modoRegistro === 'completo') return;
    const tempPedido = localStorage.getItem(`agorax_pedido_catalogo_temp_${companySlug}`);
    if (tempPedido) {
      try {
        const p = JSON.parse(tempPedido);
        localStorage.removeItem(`agorax_pedido_catalogo_temp_${companySlug}`);
        let productos = [];
        if (typeof p.productos === 'string') productos = JSON.parse(p.productos);
        else if (Array.isArray(p.productos)) productos = p.productos;
        setPedidoCatalogo({
          pedidoId: p.id,
          sessionId: p.session_id,
          codigoSede: p.codigo_sede,
          productos,
        });
        cambiarTab('catalogo');
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    socketRef.current = socket;

    const onConnect = () => {
      const cid = clienteIdRef.current;
      if (cid) {
        const eid = empresaIdRef.current;
        joinClienteRooms(socket, { idcliente: cid, empresaId: eid, sessionId: sessionRef.current });
      }
    };
    const TERMINAL_STATES = ['entregado', 'rechazado', 'cancelado', 'credito_pagado'];

    const onPedidoActualizado = (data) => {
      if (data.total !== undefined || data.productos !== undefined) {
        setPedidos(prev => prev.map(p =>
          p.id === parseInt(data.pedido_id) ? { ...p, total: data.total ?? p.total, productos: data.productos ?? p.productos, estado: data.estado ?? p.estado } : p
        ));
      } else {
        fetchPedidos();
      }
      if (data.estado && TERMINAL_STATES.includes(data.estado)) {
        if (data.estado === 'cancelado') {
          setPedidoCancelado({ id: data.pedido_id, motivo: data.motivo_cancelacion || '' });
        }
        if (data.estado === 'entregado') {
          setPedidoEntregado(true);
        }
        cambiarTab('inicio');
        return;
      }
      if (data.estado && data.estado !== 'productos_actualizados') {
        agregarNotificacion({
          tipo: 'fase',
          titulo: 'Pedido actualizado',
          mensaje: `Tu pedido ahora est\u00e1: ${data.estado}`,
          pedidoId: data.pedido_id,
        });
      }
      if (data.confirmacion_pendiente) {
        agregarNotificacion({
          tipo: 'confirmacion',
          titulo: 'Confirma tu pedido',
          mensaje: `El asesor revisó tu pedido. Revisa los cambios y confirma.`,
          pedidoId: data.pedido_id,
        });
      }
    };
    const onNuevoMensaje = (data) => {
      if (data.autor === 'asesor' || data.autor === 'sistema') {
        agregarNotificacion({
          tipo: 'mensaje',
          titulo: data.autor === 'sistema' ? 'Pedido actualizado' : 'Nuevo mensaje',
          mensaje: data.mensaje?.substring(0, 80) || 'Tienes un nuevo mensaje.',
          pedidoId: data.pedido_id,
        });
        playNotificationSound();
        if (activeTabRef.current !== 'chat') {
          setPendientesChat((prev) => prev + 1);
          mostrarToastHome(
            data.autor === 'sistema' ? 'Pedido actualizado' : 'Nuevo mensaje del asesor',
            data.mensaje?.substring(0, 100) || 'Tienes un nuevo mensaje.'
          );
        }
      }
    };

    const onCartUpdate = (data) => {
      const sid = sessionRef.current;
      const slug = companyRef.current;
      if (data.session_id && data.session_id !== sid) return;
      setPedidos(prev => prev.map(p => {
        const pidStr = localStorage.getItem(`agorax_pedido_activo_${slug}`);
        if (pidStr && parseInt(p.id) === parseInt(pidStr)) {
          const carrito = data.carrito || [];
          const newTotal = carrito.reduce((s, c) => {
            const base = c.pesable ? (c.precio*c.cantidad)/1000 : c.precio*c.cantidad;
            const etiqExtra = c.etiquetas_extra || (c.etiquetas||[]).reduce((t,e)=>t+(e.precio||0),0);
            return s + base + etiqExtra;
          }, 0);
          return { ...p, total: newTotal, productos: carrito };
        }
        return p;
      }));
    };

    if (!socket.connected) socket.connect();
    socket.on('connect', onConnect);
    socket.on('pedido_actualizado', onPedidoActualizado);
    socket.on('nuevo_mensaje', onNuevoMensaje);
    socket.on('cart_update', onCartUpdate);
    if (socket.connected) onConnect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('pedido_actualizado', onPedidoActualizado);
      socket.off('nuevo_mensaje', onNuevoMensaje);
      socket.off('cart_update', onCartUpdate);
    };
    // eslint-disable-next-line
  }, [sessionId, schemaName]);

  const location = useLocation();
  useEffect(() => {
    fetchPedidos();
    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchPedidos();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!pedidoEntregado) return;
    const t = setTimeout(() => setPedidoEntregado(false), 6000);
    return () => clearTimeout(t);
  }, [pedidoEntregado]);

  const mostrarToastHome = (titulo, texto) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ id: Date.now(), titulo, texto });
    toastTimerRef.current = setTimeout(() => setToast(null), 4500);
  };

  const handleRefreshPull = async () => {
    setRefrescando(true);
    await fetchPedidos();
    setRefrescando(false);
  };

  const handleTouchStart = (e) => {
    pullStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = async (e) => {
    if (activeTab === 'catalogo') return;
    const delta = e.changedTouches[0].clientY - pullStartY.current;
    if (delta > 80 && mainRef.current && mainRef.current.scrollTop <= 10 && !refrescando) {
      handleRefreshPull();
    }
  };

  const nombreCorto = clienteData?.nombre ? clienteData.nombre.split(' ')[0] : 'Cliente';

  const enRevision =
    clienteData?.estado_aprobacion === 'pendiente' ||
    clienteData?.estado_aprobacion === 'rechazado';

  const necesitaRegistro =
    !verificandoRegistro &&
    (!clienteData ||
      !clienteData.nombre ||
      clienteData.nombre === 'Cliente Catalogo' ||
      clienteData.nombre === 'Nuevo Cliente' ||
      (!clienteData.usuario && (!clienteData.telefono || !clienteData.cedula)));

  const necesitaAuthCompleto =
    modoRegistro === 'completo' &&
    (necesitaRegistro || (!verificandoRegistro && clienteData && !clienteData.usuario));

  const handleLoginExitoso = (data) => {
    reconnectSocketWithAuth();
    if (data.session_id) {
      localStorage.setItem(`agorax_session_${companySlug}`, data.session_id);
      setSessionId(data.session_id);
    }
    if (data.empresa_id) empresaIdRef.current = data.empresa_id;
    if (data.id) clienteIdRef.current = data.id;
    setClienteData(data);
    localStorage.setItem(`agorax_cliente_id_${companySlug}`, data.id);
    localStorage.removeItem(
      `agorax_cart_${localStorage.getItem(`agorax_session_${companySlug}`) || ''}`
    );
    localStorage.removeItem(`agorax_pedido_activo_${companySlug}`);
    localStorage.removeItem(`agorax_pedido_seleccionado_${companySlug}`);
    localStorage.removeItem(`agorax_pedido_catalogo_temp`);
    setPedidoCatalogo(null);
    if (socketRef.current && data.id) {
      joinClienteRooms(socketRef.current, {
        idcliente: data.id,
        empresaId: data.empresa_id,
        sessionId: data.session_id,
      });
    }
    setAuthScreen(null);
    setVerificandoRegistro(false);
    fetchPedidos();
  };

  const handleRegistroCompleto = (data) => {
    reconnectSocketWithAuth();
    if (data.session_id) {
      localStorage.setItem(`agorax_session_${companySlug}`, data.session_id);
      setSessionId(data.session_id);
    }
    if (data.empresa_id) empresaIdRef.current = data.empresa_id;
    if (data.id) clienteIdRef.current = data.id;
    setClienteData(data);
    localStorage.setItem(`agorax_cliente_id_${companySlug}`, data.id);
    localStorage.removeItem(
      `agorax_cart_${localStorage.getItem(`agorax_session_${companySlug}`) || ''}`
    );
    localStorage.removeItem(`agorax_pedido_activo_${companySlug}`);
    localStorage.removeItem(`agorax_pedido_seleccionado_${companySlug}`);
    localStorage.removeItem(`agorax_pedido_catalogo_temp`);
    setPedidoCatalogo(null);
    if (socketRef.current && data.id) {
      joinClienteRooms(socketRef.current, {
        idcliente: data.id,
        empresaId: data.empresa_id,
        sessionId: data.session_id,
      });
    }
    setAuthScreen(null);
    setVerificandoRegistro(false);
    fetchPedidos();
  };

  const handleLogoutCliente = () => {
    localStorage.removeItem(`agorax_session_${companySlug}`);
    localStorage.removeItem(`agorax_cliente_id_${companySlug}`);
    localStorage.removeItem(`agorax_pedido_activo_${companySlug}`);
    localStorage.removeItem(`agorax_pedido_seleccionado_${companySlug}`);
    setPedidoSeleccionadoId(null);
    setClienteData(null);
    setSessionId(null);
    setAuthScreen('login');
    const newSid = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem(`agorax_session_${companySlug}`, newSid);
    setSessionId(newSid);
    setVerificandoRegistro(true);
    setTimeout(() => setVerificandoRegistro(false), 100);
  };

  const pedidosActivos = pedidos.filter(
    (p) => !['entregado','rechazado','cancelado','credito_pagado'].includes(p.estado)
  );
  const pedidosHistorial = pedidos.filter(
    (p) => ['entregado','rechazado','cancelado','credito_pagado'].includes(p.estado)
  );

  useEffect(() => {
    if (!cargandoPedidos && pedidosActivos.length === 0 && sessionId) {
      localStorage.removeItem(`agorax_cart_${sessionId}`);
      localStorage.removeItem(`agorax_pedido_activo_${companySlug}`);
      localStorage.removeItem(`agorax_cart_confirm_${companySlug}`);
    }
  }, [pedidosActivos.length, cargandoPedidos, sessionId, companySlug]);

  const pedidoActivo = pedidosActivos.length > 0 ? pedidosActivos[0] : null;
  // La empresa puede desactivar pedidos simultaneos: solo se permite un pedido
  // activo a la vez. Se habilita el boton de nuevo pedido solo si no hay activos.
  const permiteNuevoPedido = config?.permite_pedidos_simultaneos !== false || pedidosActivos.length === 0;
  // Pedido seleccionado para mostrar en el detalle/chat/catalogo (permite alternar
  // entre multiples pedidos activos en paralelo). Si el seleccionado ya no esta
  // activo, se cae al pedido activo mas reciente. `modoNuevoPedido` desactiva el
  // fallback para poder iniciar un pedido nuevo sin seleccion.
  const pedidoSeleccionado = modoNuevoPedido
    ? null
    : pedidosActivos.find((p) => parseInt(p.id) === parseInt(pedidoSeleccionadoId)) || pedidoActivo || null;

  // Orden cuyo estado se refleja en el piloto flotante de estado.
  const pedidoReflejado = pedidoSeleccionado || pedidoActivo;
  const faseActual = pedidoReflejado ? faseInfo(pedidoReflejado.estado) : null;
  const handleSeleccionarPedido = useCallback((id) => {
    if (id === null || id === undefined) {
      // Deseleccionar => modo "nuevo pedido" (sin pedido activo seleccionado).
      setModoNuevoPedido(true);
      setPedidoSeleccionadoId(null);
      localStorage.removeItem(`agorax_pedido_seleccionado_${companySlug}`);
      return;
    }
    setModoNuevoPedido(false);
    setPedidoSeleccionadoId(id);
    localStorage.setItem(`agorax_pedido_seleccionado_${companySlug}`, String(id));
  }, [companySlug]);
  const handleNuevoPedido = useCallback(() => {
    setModoNuevoPedido(true);
    setPedidoSeleccionadoId(null);
    localStorage.removeItem(`agorax_pedido_seleccionado_${companySlug}`);
    setPedidoCatalogo(null);
    cambiarTab('catalogo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug]);
  const pedidoDetalleId = pedidoConfirmandoId || pedidoSeleccionado?.id;
  const esClienteCorporativo = clienteData?.tipo === 'corporativo' || !!clienteData?.es_corporativo;
  const pedidoDetalleCorp = pedidoDetalleId
    ? pedidosActivos.find((p) => parseInt(p.id) === parseInt(pedidoDetalleId) && p.tipo_pedido === 'corporativo')
    : null;
  const grupoCorpId = pedidoDetalleCorp?.grupo_cobro
    || (esClienteCorporativo
      ? pedidosActivos.find((p) => p.tipo_pedido === 'corporativo' && p.grupo_cobro)?.grupo_cobro
      : null);
  const pedidosGrupoCorp = grupoCorpId
    ? pedidosActivos.filter((p) => p.grupo_cobro === grupoCorpId)
    : [];
  const pedidoBloqueado = pedidoSeleccionado && pedidoSeleccionado.estado !== 'pendiente'
    && !(pedidoSeleccionado.estado === 'en_revision' && config?.cliente_puede_modificar_en_revision);
  const pedidoConfirma = pedidosActivos.find((p) => p.confirmacion_pendiente);

  const pedidoPendienteConfirmar = pedidoActivo && pedidoActivo.estado === 'pendiente' ? pedidoActivo : null;
  const productosPendiente = pedidoPendienteConfirmar?.productos
    ? (Array.isArray(pedidoPendienteConfirmar.productos) ? pedidoPendienteConfirmar.productos
      : (typeof pedidoPendienteConfirmar.productos === 'string' ? (() => { try { return JSON.parse(pedidoPendienteConfirmar.productos); } catch { return []; } })() : []))
    : [];
  const pendienteCount = productosPendiente.length;

  useEffect(() => {
    if (pedidoPendienteConfirmar?.id) setBannerDismissed(false);
  }, [pedidoPendienteConfirmar?.id]);

  const cambiarTab = (tab) => {
    if (tab === 'catalogo' && config.catalogo_habilitado === false) tab = 'inicio';
    if (tab === 'chat' && config.chat_habilitado === false) tab = 'inicio';
    if (tab === 'chat') setPendientesChat(0);
    if (tab !== 'pedido') setPedidoConfirmandoId(null);
    if (tab !== 'catalogo') setModoNuevoPedido(false);
    if (tab !== activeTabRef.current) setBannerDismissed(false);
    if (tab !== activeTabRef.current) setPilotoDismissed(false);
    activeTabRef.current = tab;
    setActiveTab(tab);
  };

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const irAConfirmarPedido = (pid) => {
    if (pid) setPedidoConfirmandoId(pid);
    fetchPedidos();
    setAbrirConfirmacion(true);
    cambiarTab('pedido');
  };

  const cerrarTutorial = () => {
    setTutorialActivo(false);
    localStorage.setItem(`agorax_tutorial_visto_${companySlug}`, '1');
  };

  const finalizarTutorial = () => {
    cerrarTutorial();
    cambiarTab('catalogo');
  };

  const onPasoTutorial = (p) => {
    if (p && p.tab) cambiarTab(p.tab);
  };

  const pasosTutorial = [
    {
      titulo: 'Bienvenido a {brand}',
      texto: 'Te mostramos como crear y procesar tu pedido en pocos pasos.',
      target: null,
      tab: 'inicio',
    },
    {
      titulo: 'Inicio',
      texto: 'Aqui ves tus pedidos activos y tu historial de compras.',
      target: '[data-tutorial="tab-inicio"]',
      tab: 'inicio',
    },
    ...(config.catalogo_habilitado !== false
      ? [
          {
            titulo: 'Catalogo',
            texto: 'Explora todos los productos. Puedes buscar y filtrar por categoría.',
            target: '[data-tutorial="tab-catalogo"]',
            tab: 'catalogo',
          },
          {
            titulo: 'Agrega productos',
            texto:
              'Toca un producto para ver su detalle y agregarlo. Tu pedido se crea automaticamente.',
            target: '[data-tutorial="catalogo-grid"]',
            tab: 'catalogo',
          },
          {
            titulo: 'Tu carrito',
            texto: 'Aqui se acumula tu pedido. Abrelo para revisarlo y pulsa "Confirmar pedido".',
            target: '[data-tutorial="carrito-fab"]',
            tab: 'catalogo',
          },
        ]
      : []),
    ...(config.chat_habilitado !== false
      ? [
          {
            titulo: 'Chat asistente',
            texto:
              'Preguntale al asistente por productos, precios u horarios. Tambien puedes armar tu pedido conversando.',
            target: '[data-tutorial="tab-chat"]',
            tab: 'chat',
          },
        ]
      : []),
    {
      titulo: 'Tu pedido',
      texto:
        'Aqui confirmas, eliges el método de pago y subes tu comprobante para procesar el pedido.',
      target: '[data-tutorial="tab-pedido"]',
      tab: 'inicio',
    },
    {
      titulo: 'Ayuda',
      texto: 'Puedes repetir este tutorial cuando quieras tocando este boton.',
      target: '[data-tutorial="btn-ayuda"]',
      tab: 'inicio',
    },
  ];

  useEffect(() => {
    if (verificandoRegistro || necesitaRegistro) return;
    const visto = localStorage.getItem(`agorax_tutorial_visto_${companySlug}`);
    if (!visto) {
      const t = setTimeout(() => setTutorialActivo(true), 700);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificandoRegistro, necesitaRegistro, companySlug]);

  const tabs = [
    { id: 'inicio', icon: FiHome, label: 'Inicio' },
    ...(config.catalogo_habilitado !== false
      ? [{ id: 'catalogo', icon: FiGrid, label: 'Catálogo' }]
      : []),
    ...(config.chat_habilitado !== false
      ? [{ id: 'chat', icon: FiMessageSquare, label: 'Chat' }]
      : []),
    { id: 'pedido', icon: FiInfo, label: 'Pedido' },
    ...(modoRegistro === 'completo' ? [{ id: 'perfil', icon: FiSettings, label: 'Perfil' }] : []),
  ];

  const sidebarContent = (
    <div style={SD.sidebar}>
      <div style={SD.sidebarLogo}>
        <img
          src={
            config?.logo_url
              ? getImagenUrl(config.logo_url)
              : '/logo-agorax.jpeg'
          }
          alt={brandName}
          style={SD.logoImg}
        />
        <div style={SD.logoText}>{brandName}</div>
      </div>

      <nav style={SD.nav}>
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => cambiarTab(tab.id)}
              data-tutorial={`tab-${tab.id}`}
              style={{
                ...SD.navItem,
                ...(active ? SD.navItemActive : {}),
              }}
            >
              <tab.icon size={20} strokeWidth={active ? 2.5 : 2} />
              <span style={SD.navLabel}>{tab.label}</span>
              {tab.id === 'pedido' && pendienteCount > 0 && (
                <span style={{
                  ...SD.sidebarBadge, background: '#ea4335', color: '#fff',
                  animation: 'bannerSlideIn 0.3s ease',
                }}>
                  {pendienteCount > 99 ? '99+' : pendienteCount}
                </span>
              )}
              {tab.id === 'chat' && pendientesChat > 0 && (
                <span style={SD.sidebarBadge}>{pendientesChat > 9 ? '9+' : pendientesChat}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div style={SD.sidebarFooter}>
        <div
          style={{ ...SD.userInfo, ...(modoRegistro === 'completo' ? { cursor: 'pointer' } : {}) }}
          onClick={() => modoRegistro === 'completo' && cambiarTab('perfil')}
        >
          <div style={SD.userAvatar}>
            <FiUser size={18} />
          </div>
          <div>
            <div style={SD.userName}>{nombreCorto}</div>
            <div style={SD.userRol}>Cliente</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (verificandoRegistro) {
    return (
      <div style={S.wrapper}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '3px solid var(--color-border)',
              borderTop: '3px solid var(--color-secondary)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        </div>
        <style>{cssAnimations}</style>
      </div>
    );
  }

  if (enRevision) {
    const rechazado = clienteData?.estado_aprobacion === 'rechazado';
    return (
      <div style={SRevision.wrapper}>
        <div style={SRevision.card}>
          <img
            src={
              config?.logo_url
                ? getImagenUrl(config.logo_url)
                : '/logo-agorax.jpeg'
            }
            alt={brandName}
            style={SRevision.logoImg}
          />
          <div style={SRevision.icon}>
            {rechazado ? (
              <FiXCircle size={44} color="#ea4335" />
            ) : (
              <FiClock size={44} color="var(--color-secondary)" />
            )}
          </div>
          <h1 style={SRevision.title}>
            {rechazado ? 'Solicitud rechazada' : 'Tu perfil está en verificación'}
          </h1>
          <p style={SRevision.text}>
            {rechazado
              ? clienteData?.motivo_rechazo
                ? `Tu solicitud fue rechazada: ${clienteData.motivo_rechazo}`
                : 'Tu solicitud de registro fue rechazada. Puedes volver a intentarlo.'
              : 'Tu solicitud de registro está en revisión. La empresa revisará tus datos y te avisará cuando tu acceso sea aprobado.'}
          </p>
          {rechazado && (
            <button onClick={handleLogoutCliente} style={SRevision.btn}>
              Volver a registrarme
            </button>
          )}
        </div>
      </div>
    );
  }

  if (necesitaAuthCompleto) {
    if (authScreen === 'register') {
      return (
        <RegistroClienteCompleto
          sessionId={sessionId}
          schemaName={schemaName}
          brandName={brandName}
          logoUrl={config?.logo_url}
          onRegistroExitoso={handleRegistroCompleto}
          onVolverLogin={() => {
            setAuthScreen('login');
          }}
        />
      );
    }
    if (authScreen === 'recover') {
      return (
        <RecuperarCliente
          schemaName={schemaName}
          brandName={brandName}
          logoUrl={config?.logo_url}
          tokenInicial={recoveryTokenUrl}
          onVolverLogin={() => {
            setAuthScreen('login');
            setRecoveryTokenUrl(null);
          }}
        />
      );
    }
    return (
      <LoginCliente
        schemaName={schemaName}
        brandName={brandName}
        logoUrl={config?.logo_url}
        modoRegistro={modoRegistro}
        onLoginExitoso={handleLoginExitoso}
        onIrARegistro={() => setAuthScreen('register')}
        onIrARecuperar={() => setAuthScreen('recover')}
        onVolverSimple={() => {
          setAuthScreen(null);
          setVerificandoRegistro(true);
          setTimeout(() => setVerificandoRegistro(false), 100);
        }}
      />
    );
  }

  if (necesitaRegistro) {
    return (
      <RegistroCliente
        sessionId={sessionId}
        schemaName={schemaName}
        brandName={brandName}
        logoUrl={config?.logo_url}
        onCompletado={handleRegistroCompleto}
      />
    );
  }

  return (
    <div
      style={{
        ...S.wrapper,
        flexDirection: isDesktop ? 'row' : 'column',
        maxWidth: isDesktop ? '100%' : '900px',
      }}
    >
      {isDesktop && sidebarContent}

      <div
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, height: '100%' }}
      >
        <header style={S.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src={
                config?.logo_url
                  ? getImagenUrl(config.logo_url)
                  : '/logo-agorax.jpeg'
              }
              alt={brandName}
              style={{ width: '32px', height: '32px', borderRadius: '8px', objectFit: 'contain' }}
            />
            <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.3px' }}>
              {brandName}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setTutorialActivo(true)}
              data-tutorial="btn-ayuda"
              title="Ver tutorial"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                color: 'var(--color-surface)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiHelpCircle size={20} />
            </button>
            <CampanaNotificaciones onVerPedido={() => cambiarTab('pedido')} />
          </div>
        </header>

        {pedidoConfirma && (
          <div
            onClick={() => cambiarTab('pedido')}
            style={{
              padding: '10px 16px',
              backgroundColor: '#fef7e0',
              borderBottom: '1px solid #fdd835',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
            }}
          >
            <FiCheckCircle size={18} color="#fbbc04" />
            <div style={{ flex: 1, fontSize: '12px', color: 'var(--color-text-secondary)' }}>
              <strong>Tu pedido</strong> — El asesor hizo cambios. Toca para
              revisar y confirmar.
            </div>
            <FiArrowRight size={16} color="#fbbc04" />
          </div>
        )}

        {config && config.chat_disponible === false && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#e8eaef',
              borderBottom: '1px solid #d0d5dd',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <FiMessageSquare size={18} color="#888" />
            <div style={{ flex: 1, fontSize: '12px', color: '#666' }}>
              <strong>Chat no disponible</strong> — El administrador aun no ha configurado la
              inteligencia artificial para esta empresa.
            </div>
          </div>
        )}

        {toast && (
          <div onClick={() => cambiarTab('chat')} style={S.toastBanner}>
            <FiMessageCircle size={18} color="#fff" />
            <div style={{ flex: 1 }}>
              <div style={S.toastTitulo}>{toast.titulo}</div>
              <div style={S.toastTexto}>{toast.texto}</div>
            </div>
            <div style={S.toastCta}>Ver chat</div>
          </div>
        )}

        {(faseActual || pedidoEntregado) && !pilotoDismissed && !pedidoConfirma && !toast && (() => {
          const currentIdx = pedidoEntregado
            ? FASES_STEPPER.length - 1
            : faseStepIndex(pedidoReflejado?.estado);
          return (
            <div
              onClick={() => cambiarTab('pedido')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') cambiarTab('pedido');
              }}
              style={{
                margin: '10px 16px 0',
                padding: '12px 16px 14px',
                borderRadius: '14px',
                background: pedidoEntregado
                  ? 'linear-gradient(135deg, var(--color-success), #059669)'
                  : 'var(--color-surface)',
                color: pedidoEntregado ? '#fff' : 'var(--color-primary)',
                boxShadow: '0 4px 16px rgba(11,26,74,0.14)',
                border: pedidoEntregado ? 'none' : '1px solid var(--color-border)',
                cursor: 'pointer',
                animation: 'slideDown 0.3s ease',
                outline: 'none',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: pedidoEntregado ? 'rgba(255,255,255,0.2)' : 'rgba(41,82,204,0.1)',
                    color: pedidoEntregado ? '#fff' : (faseActual?.color || 'var(--color-secondary)'),
                  }}
                >
                  {pedidoEntregado ? (
                    <FiCheckCircle size={18} />
                  ) : faseActual ? (
                    <faseActual.Icon size={18} />
                  ) : null}
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      opacity: pedidoEntregado ? 0.85 : 0.5,
                    }}
                  >
                    Tu pedido
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {pedidoEntregado ? '¡Listo! Pedido entregado' : faseActual?.texto}
                  </span>
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPilotoDismissed(true);
                  }}
                  aria-label="Cerrar aviso"
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    background: pedidoEntregado ? 'rgba(255,255,255,0.25)' : 'var(--color-bg-hover)',
                    color: pedidoEntregado ? '#fff' : 'var(--color-text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    padding: 0,
                  }}
                >
                  <FiX size={12} />
                </button>
                <FiArrowRight size={16} style={{ flexShrink: 0, opacity: 0.6 }} />
              </div>

              {/* Stepper de progreso */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '12px' }}>
                {FASES_STEPPER.map((s, i) => {
                  const done = i < currentIdx || pedidoEntregado;
                  const active = i === currentIdx;
                  return (
                    <div
                      key={s.label}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: done
                            ? 'var(--color-success)'
                            : active
                              ? pedidoEntregado
                                ? 'var(--color-success)'
                                : 'var(--color-secondary)'
                              : 'var(--color-bg-hover)',
                          color: done || active ? '#fff' : 'var(--color-text-muted)',
                          boxShadow: active && !done ? '0 0 0 3px rgba(41,82,204,0.15)' : 'none',
                        }}
                      >
                        {done ? <FiCheck size={11} /> : i + 1}
                      </div>
                      <div
                        style={{
                          fontSize: '9px',
                          fontWeight: active ? 700 : 500,
                          color: pedidoEntregado
                            ? 'rgba(255,255,255,0.9)'
                            : active
                              ? 'var(--color-secondary)'
                              : 'var(--color-text-muted)',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <main
          ref={mainRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={S.content}
        >
          {pedidoPendienteConfirmar && pendienteCount > 0 && !bannerDismissed && (
            <div style={{
              position: 'sticky', top: 0, zIndex: 5,
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 14, margin: '8px 12px',
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              boxShadow: '0 4px 20px rgba(37,82,204,0.3)', color: '#fff',
              animation: 'bannerSlideIn 0.4s ease',
            }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiShoppingBag size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.95 }}>Pedido listo</div>
                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 1 }}>
                  {pendienteCount} producto{pendienteCount !== 1 ? 's' : ''}
                </div>
              </div>
              <button onClick={() => irAConfirmarPedido(pedidoPendienteConfirmar.id)} style={{
                padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: '#fff', color: 'var(--color-primary)', fontSize: 11, fontWeight: 700,
                whiteSpace: 'nowrap', fontFamily: 'inherit',
              }}>
                Confirmar
              </button>
              <button onClick={() => setBannerDismissed(true)} style={{
                width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.15)', color: '#fff', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <FiX size={10} />
              </button>
            </div>
          )}
          {refrescando && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px 0 8px',
                gap: '10px',
              }}
            >
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  border: '2.5px solid var(--color-border)',
                  borderTopColor: 'var(--color-secondary)',
                  animation: 'spinRefresh 0.7s linear infinite',
                }}
              />
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--color-secondary)',
                  letterSpacing: '0.2px',
                }}
              >
                Actualizando
              </span>
            </div>
          )}
          {!pushActivo && clienteData?.id && sessionId && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                margin: '8px 16px',
                padding: '12px 14px',
                borderRadius: '12px',
                background: 'var(--color-primary)',
                color: '#fff',
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.4 }}>
                Activa las notificaciones para enterarte del estado de tus pedidos.
              </span>
              <button
                onClick={activarPush}
                disabled={pushSolicitando}
                style={{
                  background: '#fff',
                  color: 'var(--color-primary)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '8px 14px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  opacity: pushSolicitando ? 0.6 : 1,
                }}
              >
                {pushSolicitando ? '...' : 'Activar'}
              </button>
            </div>
          )}
          {activeTab === 'inicio' && (
            <ClienteInicio
              pedidosActivos={pedidosActivos}
              pedidosHistorial={pedidosHistorial}
              cargando={cargandoPedidos}
              onNewOrder={handleNuevoPedido}
              abriendoPedido={false}
              onVerPedido={(id) => { if (id) setPedidoConfirmandoId(id); cambiarTab('pedido'); }}
              permiteNuevoPedido={permiteNuevoPedido}
              schemaName={schemaName}
              onVerNuevos={() => { setVerNuevos(true); setActiveTab('catalogo'); }}
            />
          )}
          {activeTab === 'catalogo' && (
            <ClienteCatalogo
              config={config}
              sessionId={sessionId}
              schemaName={schemaName}
              brandName={brandName}
              botName={botName}
              onPedidoCreado={fetchPedidos}
              pedidoInicial={pedidoCatalogo}
              pedidosPendientes={pedidosActivos}
              onPedidoInicialConsumido={() => setPedidoCatalogo(null)}
              onVolverInicio={() => setActiveTab('inicio')}
              bloqueado={pedidoBloqueado}
              pedidoActivo={pedidoActivo}
              pedidoSeleccionadoId={pedidoSeleccionado ? pedidoSeleccionado.id : null}
              onSeleccionarPedido={handleSeleccionarPedido}
              onNuevoPedido={permiteNuevoPedido ? handleNuevoPedido : null}
              modoNuevoPedido={modoNuevoPedido}
              onConfirmarPedido={irAConfirmarPedido}
              productoInicialCodigo={productoCompartidoCodigo}
              onProductoInicialConsumido={() => setProductoCompartidoCodigo(null)}
              verNuevosInicial={verNuevos}
              onVerNuevosConsumido={() => setVerNuevos(false)}
            />
          )}
          <div style={{ display: activeTab === 'chat' ? 'contents' : 'none' }}>
            {sessionId ? (
              <ClienteChat
                config={config}
                sessionId={sessionId}
                compact={true}
                onBack={() => setActiveTab('inicio')}
                onVerPedido={() => cambiarTab('pedido')}
                onConfirmarPedido={irAConfirmarPedido}
                onPedidoConfirmado={fetchPedidos}
                brandName={brandName}
                logoUrl={config?.logo_url}
                pedidosActivos={pedidosActivos}
                pedidoSeleccionadoId={pedidoSeleccionado?.id}
                onSeleccionarPedido={handleSeleccionarPedido}
                activo={activeTab === 'chat'}
              />
            ) : (
              <div
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '3px solid var(--color-border)',
                    borderTop: '3px solid var(--color-secondary)',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              </div>
            )}
          </div>
          {activeTab === 'pedido' &&
            (pedidosGrupoCorp.length > 0 ? (
              <ClientePedidoCorporativo
                config={config}
                sessionId={sessionId}
                schemaName={schemaName}
                pedidos={pedidosGrupoCorp}
                onBack={() => setActiveTab('inicio')}
                onConfirmado={() => {
                  setAbrirConfirmacion(false);
                  setPedidoConfirmandoId(null);
                  fetchPedidos();
                }}
              />
            ) : pedidoDetalleId ? (
              <ClientePedidoDetalle
                pedidoId={pedidoDetalleId}
                inline={true}
                config={config}
                sessionId={sessionId}
                onBack={() => setActiveTab('inicio')}
                onGoToChat={() => cambiarTab('chat')}
                onGoToCatalog={() => cambiarTab('catalogo')}
                abrirConfirmacionAlMontar={abrirConfirmacion}
                onConfirmacionConsumida={() => setAbrirConfirmacion(false)}
                pedidosActivos={pedidosActivos}
                pedidoSeleccionadoId={pedidoDetalleId}
                onSeleccionarPedido={handleSeleccionarPedido}
              />
            ) : (
              <div style={S.emptyTab}>
                <div style={S.emptyTabIcon}>
                  <FiInfo size={48} color="var(--color-text-muted)" />
                </div>
                <div style={S.emptyTabTitle}>Sin pedido activo</div>
                <div style={S.emptyTabText}>
                  No tienes un pedido en curso. Crea uno desde el catálogo para ver su información
                  detallada.
                </div>
                <button onClick={() => cambiarTab('catalogo')} style={S.emptyTabBtn}>
                  Ver catálogo
                </button>
              </div>
            ))}
          {activeTab === 'perfil' && (
            <PerfilCliente
              sessionId={sessionId}
              schemaName={schemaName}
              clienteData={clienteData}
              config={config}
              onLogout={handleLogoutCliente}
              onVerCredito={() => cambiarTab('credito')}
              onPerfilActualizado={(data) =>
                setClienteData((prev) => (prev ? { ...prev, ...data } : prev))
              }
            />
          )}
          {activeTab === 'credito' && (
            <ClienteCreditos
              sessionId={sessionId}
              schemaName={schemaName}
              companySlug={companySlug}
              onBack={() => cambiarTab('inicio')}
            />
          )}
        </main>

        <InstalarApp appName={brandName} />
        {!isDesktop && (
          <nav style={S.bottomNav}>
            {tabs.map((tab) => {
              const active = activeTab === tab.id;
              return (
                  <button
                    key={tab.id}
                    onClick={() => cambiarTab(tab.id)}
                    data-tutorial={`tab-${tab.id}`}
                    style={{ ...S.navItem, ...(active ? S.navItemActive : {}) }}
                  >
                    <div style={{ position: 'relative', display: 'flex' }}>
                      <tab.icon size={20} strokeWidth={active ? 2.5 : 2} />
                      {tab.id === 'pedido' && pendienteCount > 0 && (
                        <span style={{
                          position: 'absolute', top: -8, right: -12,
                          minWidth: 18, height: 18, borderRadius: 9,
                          background: '#ea4335', color: '#fff',
                          fontSize: 10, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 4px', boxShadow: '0 2px 6px rgba(234,67,53,0.4)',
                          animation: 'bannerSlideIn 0.3s ease',
                        }}>
                          {pendienteCount > 99 ? '99+' : pendienteCount}
                        </span>
                      )}
                      {tab.id === 'chat' && pendientesChat > 0 && (
                        <span style={S.chatBadge}>{pendientesChat > 9 ? '9+' : pendientesChat}</span>
                      )}
                    </div>
                    {active && <span style={S.navLabel}>{tab.label}</span>}
                  </button>
              );
            })}
          </nav>
        )}

      </div>
      <style>{cssAnimations}</style>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bannerSlideIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {pedidoCancelado && (
        <div
          onClick={() => setPedidoCancelado(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: '20px',
              padding: '28px 24px',
              maxWidth: '380px',
              width: '100%',
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
              textAlign: 'center',
            }}
          >
            <FiXCircle size={48} style={{ marginBottom: '12px', color: 'var(--color-danger)' }} />
            <h3
              style={{
                margin: '0 0 8px',
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--color-primary)',
              }}
            >
              Pedido cancelado
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                margin: '0 0 4px',
                lineHeight: 1.4,
              }}
            >
              Tu pedido ha sido cancelado por un asesor.
            </p>
            {pedidoCancelado.motivo && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px 14px',
                  backgroundColor: '#fef7f7',
                  borderRadius: '10px',
                  border: '1px solid #f5c6cb',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--color-danger)',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                  }}
                >
                  Motivo
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.4,
                  }}
                >
                  {pedidoCancelado.motivo}
                </div>
              </div>
            )}
            <button
              onClick={() => setPedidoCancelado(null)}
              style={{
                marginTop: '18px',
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                border: 'none',
                backgroundColor: 'var(--color-secondary)',
                color: 'var(--color-surface)',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <TutorialTour
        activo={tutorialActivo}
        pasos={pasosTutorial}
        onCambioPaso={onPasoTutorial}
        onCerrar={cerrarTutorial}
        onFinalizar={finalizarTutorial}
        brandName={brandName}
      />
    </div>
  );
}

const SD = {
  sidebar: {
    width: '250px',
    minWidth: '250px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: '2px 0 24px rgba(11,26,74,0.15)',
    zIndex: 10,
  },
  sidebarLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 20px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  logoImg: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  logoText: {
    fontSize: '17px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
  },
  nav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '16px 12px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.65)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
    textAlign: 'left',
    width: '100%',
  },
  navItemActive: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: 'var(--color-surface)',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  navLabel: {
    flex: 1,
  },
  sidebarBadge: {
    backgroundColor: 'var(--color-danger)',
    color: 'var(--color-surface)',
    fontSize: '10px',
    fontWeight: 700,
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
  },
  sidebarFooter: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  userName: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  userRol: {
    fontSize: '11px',
    opacity: 0.6,
  },
};

const S = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: 'var(--color-bg)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    minHeight: '56px',
  },
  content: { flex: 1, overflow: 'auto', minHeight: 0, paddingBottom: '0px' },
  bottomNav: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'var(--color-primary)',
    borderRadius: '24px',
    margin: '0 16px 12px',
    marginBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    padding: '6px',
    boxShadow: '0 8px 32px rgba(11,26,74,0.25)',
    flexShrink: 0,
  },
  navItem: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    borderRadius: '20px',
    color: 'rgba(255,255,255,0.55)',
    transition: 'all 0.25s ease',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    flex: 0,
    minWidth: '48px',
    justifyContent: 'center',
  },
  navItemActive: {
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-primary)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    flex: 1,
  },
  navLabel: { fontSize: '12px', fontWeight: 600, lineHeight: 1 },
  emptyTab: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    textAlign: 'center',
    height: '100%',
    gap: '12px',
  },
  emptyTabIcon: {
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTabTitle: { fontSize: '18px', fontWeight: 700, color: 'var(--color-primary)' },
  emptyTabText: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
    maxWidth: '280px',
  },
  emptyTabBtn: {
    marginTop: '8px',
    padding: '12px 28px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  chatBadge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: 'var(--color-danger)',
    color: 'var(--color-surface)',
    fontSize: '10px',
    fontWeight: 700,
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  toastBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    margin: '10px 16px 0',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    borderRadius: '14px',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(41,82,204,0.35)',
    animation: 'slideDown 0.3s ease',
    flexShrink: 0,
  },
  toastTitulo: { fontSize: '13px', fontWeight: 700 },
  toastTexto: {
    fontSize: '12px',
    opacity: 0.85,
    marginTop: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  toastCta: {
    fontSize: '12px',
    fontWeight: 700,
    color: 'var(--color-surface)',
    opacity: 0.8,
    whiteSpace: 'nowrap',
  },
};

const SRevision = {
  wrapper: {
    height: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: 'auto',
    padding: '24px 20px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '24px',
    padding: '40px 32px 32px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 4px 32px rgba(11,26,74,0.08)',
    textAlign: 'center',
  },
  logoImg: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    objectFit: 'contain',
    marginBottom: '16px',
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 800,
    color: 'var(--color-primary)',
    margin: '0 0 10px',
    letterSpacing: '-0.3px',
  },
  text: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.55,
    margin: '0 0 26px',
  },
  btn: {
    padding: '14px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: '100%',
  },
};

export default ClienteHome;
